import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { db } from '../../firebase';
import {
  collection, onSnapshot, addDoc, serverTimestamp,
  query, orderBy, doc, updateDoc, deleteDoc, arrayUnion, arrayRemove, increment
} from 'firebase/firestore';
import UserAvatar from '../common/UserAvatar.jsx';
import './Dialogs.css';

const REACTIONS = [
  { key: 'like', emoji: '👍', label: 'Лайк' },
  { key: 'dislike', emoji: '👎', label: 'Дизлайк' },
];

const formatDateTime = (ts) => {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString('ru-RU', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const useSmartInput = (text, setText) => {
  const handleChange = (e) => {
    let v = e.target.value;
    if (v.startsWith('... ')) v = 'Уважаемые коллеги! ' + v.slice(4);
    else if (v.startsWith('. ')) v = 'Добрый день! ' + v.slice(2);
    setText(v);
  };
  const startVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      alert('Голосовой ввод не поддерживается этим браузером. Откройте сайт в Chrome (Android) или Edge.');
      return;
    }
    const startRecognition = () => {
      const rec = new SR();
      rec.lang = 'ru-RU';
      rec.continuous = false;
      rec.interimResults = false;
      rec.onresult = (e) => {
        const t = e.results[0][0].transcript;
        setText((prev) => prev ? prev + ' ' + t : t);
      };
      rec.onerror = (e) => {
        if (e.error === 'not-allowed' || e.error === 'service-not-allowed') {
          alert('Нет доступа к микрофону. Нажмите на значок 🔒 слева от адреса сайта и разрешите микрофон.');
        } else {
          alert('Не удалось распознать речь (' + e.error + '). Попробуйте ещё раз.');
        }
      };
      rec.start();
    };
    // Явно запрашиваем разрешение на микрофон (важно для Android)
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      navigator.mediaDevices.getUserMedia({ audio: true })
        .then((stream) => { stream.getTracks().forEach((t) => t.stop()); startRecognition(); })
        .catch(() => alert('Нет доступа к микрофону. Разрешите доступ в настройках браузера (🔒 слева от адреса сайта).'));
    } else {
      startRecognition();
    }
  };
  return { handleChange, startVoice };
};

const Dialogs = () => {
  const currentUser = useSelector((state) => state.auth.user);
  const { userId } = useParams();

  const [users, setUsers] = useState([]);
  const [activeUserId, setActiveUserId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessageText, setNewMessageText] = useState('');
  const [editingMsgId, setEditingMsgId] = useState(null);
  const [editText, setEditText] = useState('');
  const [editDate, setEditDate] = useState('');
  const [editTime, setEditTime] = useState('');
  const [editMsgTopic, setEditMsgTopic] = useState('');
  const [uploadingFile, setUploadingFile] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [eventTime, setEventTime] = useState('');

  const [monologTopics, setMonologTopics] = useState([]);
  const [monologTopic, setMonologTopic] = useState('all');
  const [showMonologTopicForm, setShowMonologTopicForm] = useState(false);
  const [newMonologTopicName, setNewMonologTopicName] = useState('');
  const [editMonologTopicId, setEditMonologTopicId] = useState(null);
  const [editMonologTopicName, setEditMonologTopicName] = useState('');

  const fileInputRef = useRef(null);
  const chatScrollRef = useRef(null);
  const { handleChange: handleTextChange, startVoice } = useSmartInput(newMessageText, setNewMessageText);
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'moderator';
  const isMonolog = activeUserId === currentUser?.uid;
  const isBlocked = !!currentUser?.blocked;

  useEffect(() => { if (userId) setActiveUserId(userId); }, [userId]);

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'users'), (snapshot) => {
      const all = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setUsers(all.filter((u) => u.id !== currentUser?.uid && !u.deleted));
    }, (error) => console.error('Ошибка загрузки пользователей:', error));
    return () => unsubscribe();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser?.uid) return;
    const unsubscribe = onSnapshot(
      collection(db, 'users', currentUser.uid, 'monologTopics'),
      (snap) => setMonologTopics(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (error) => console.error('Ошибка загрузки тем монолога:', error)
    );
    return () => unsubscribe();
  }, [currentUser?.uid]);

  const chatId = currentUser && activeUserId
    ? [currentUser.uid, activeUserId].sort().join('_')
    : null;

  const activeUser = users.find((u) => u.id === activeUserId);

  useEffect(() => {
    if (!chatId) { setMessages([]); return; }
    const q = query(collection(db, 'chats', chatId, 'messages'), orderBy('createdAt', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setMessages(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    }, (error) => console.error('Ошибка загрузки чата:', error));
    return () => unsubscribe();
  }, [chatId]);
    // Автопрокрутка к последним сообщениям (при открытии чата и при новых сообщениях)
  useEffect(() => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }
  }, [messages.length, chatId]);

  useEffect(() => {
    if (!activeUserId || !currentUser?.uid || isMonolog) return;
    if ((currentUser?.unreadFrom?.[activeUserId] || 0) > 0) {
      updateDoc(doc(db, 'users', currentUser.uid), {
        [`unreadFrom.${activeUserId}`]: 0,
      }).catch(() => {});
    }
  }, [chatId, activeUserId, currentUser, isMonolog]);

  const createMonologTopic = async () => {
    if (!newMonologTopicName.trim()) return;
    try {
      await addDoc(collection(db, 'users', currentUser.uid, 'monologTopics'), {
        name: newMonologTopicName.trim(),
        createdAt: serverTimestamp(),
      });
      setNewMonologTopicName('');
      setShowMonologTopicForm(false);
    } catch (error) { console.error('Ошибка создания темы:', error); }
  };

  const saveEditMonologTopic = async (id) => {
    if (!editMonologTopicName.trim()) return;
    try {
      await updateDoc(doc(db, 'users', currentUser.uid, 'monologTopics', id), { name: editMonologTopicName.trim() });
      setEditMonologTopicId(null);
    } catch (error) { console.error('Ошибка переименования темы:', error); }
  };

  const deleteMonologTopic = async (id) => {
    if (!window.confirm('Удалить тему монолога? Записи останутся в «📋 Все».')) return;
    try {
      await deleteDoc(doc(db, 'users', currentUser.uid, 'monologTopics', id));
      if (monologTopic === id) setMonologTopic('all');
    } catch (error) { console.error('Ошибка удаления темы:', error); }
  };

  const monologTopicNameById = (id) => {
    const t = monologTopics.find((t) => t.id === id);
    return t ? t.name : '—';
  };

  const handleSend = async () => {
    if (!newMessageText.trim() || !chatId) return;
    await addDoc(collection(db, 'chats', chatId, 'messages'), {
      type: 'text',
      text: newMessageText,
      eventDate: eventDate || null,
      eventTime: eventDate && eventTime ? eventTime : null,
      monologTopicId: isMonolog && monologTopic !== 'all' ? monologTopic : null,
      from: currentUser.uid,
      fromEmail: currentUser.email,
      fromName: currentUser.displayName || currentUser.email,
      to: activeUserId,
      read: false,
      createdAt: serverTimestamp(),
    });
    if (!isMonolog) {
      await updateDoc(doc(db, 'users', activeUserId), {
        [`unreadFrom.${currentUser.uid}`]: increment(1),
      });
    }
    setNewMessageText('');
    setEventDate('');
    setEventTime('');
  };

  const handleFileSend = (file) => {
    if (!chatId || !activeUserId) return;
    if (file.size > 900 * 1024) { alert('Файл слишком большой! Лимит — 900 КБ.'); return; }
    setUploadingFile(true);
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        await addDoc(collection(db, 'chats', chatId, 'messages'), {
          type: 'file',
          fileName: file.name,
          fileType: file.type,
          fileData: reader.result,
          text: `📎 ${file.name}`,
          eventDate: eventDate || null,
          eventTime: eventDate && eventTime ? eventTime : null,
          monologTopicId: isMonolog && monologTopic !== 'all' ? monologTopic : null,
          from: currentUser.uid,
          fromEmail: currentUser.email,
          fromName: currentUser.displayName || currentUser.email,
          to: activeUserId,
          read: false,
          createdAt: serverTimestamp(),
        });
        if (!isMonolog) {
          await updateDoc(doc(db, 'users', activeUserId), {
            [`unreadFrom.${currentUser.uid}`]: increment(1),
          });
        }
      } catch (error) {
        console.error('Ошибка отправки файла:', error);
        alert('Не удалось отправить файл.');
      } finally {
        setUploadingFile(false);
        setEventDate('');
        setEventTime('');
      }
    };
    reader.readAsDataURL(file);
  };

  const handleReactMessage = async (msg, key) => {
    if (!chatId) return;
    try {
      const ref = doc(db, 'chats', chatId, 'messages', msg.id);
      const reactions = msg.reactions || {};
      const current = Object.keys(reactions).find((k) => (reactions[k] || []).includes(currentUser.uid));
      const updates = {};
      if (current === key) {
        updates[`reactions.${key}`] = arrayRemove(currentUser.uid);
      } else {
        updates[`reactions.${key}`] = arrayUnion(currentUser.uid);
        if (current) updates[`reactions.${current}`] = arrayRemove(currentUser.uid);
      }
      await updateDoc(ref, updates);
    } catch (error) { console.error('Ошибка реакции:', error); }
  };

  const handleDeleteMessage = async (msg) => {
    if (!chatId) return;
    if (window.confirm('Удалить это сообщение?')) {
      try { await deleteDoc(doc(db, 'chats', chatId, 'messages', msg.id)); }
      catch (error) { console.error('Ошибка удаления:', error); }
    }
  };

  const startEdit = (msg) => {
    setEditingMsgId(msg.id);
    setEditText(msg.text);
    setEditDate(msg.eventDate || '');
    setEditTime(msg.eventTime || '');
    setEditMsgTopic(msg.monologTopicId || '');
  };

  const saveEdit = async (msg) => {
    if (!editText.trim()) return;
    try {
      await updateDoc(doc(db, 'chats', chatId, 'messages', msg.id), {
        text: editText.trim(),
        edited: true,
        eventDate: editDate || null,
        eventTime: editDate && editTime ? editTime : null,
        ...(isMonolog ? { monologTopicId: editMsgTopic || null } : {}),
      });
      setEditingMsgId(null); setEditText(''); setEditDate(''); setEditTime(''); setEditMsgTopic('');
    } catch (error) { console.error('Ошибка редактирования:', error); }
  };

  const baseMessages = searchText.trim()
    ? messages.filter((m) => (m.text || '').toLowerCase().includes(searchText.trim().toLowerCase()))
    : messages;
  const filteredMessages = isMonolog && monologTopic !== 'all'
    ? baseMessages.filter((m) => m.monologTopicId === monologTopic)
    : baseMessages;

  const smallBtn = { background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '13px', margin: '0 3px', verticalAlign: 'middle', opacity: 0.7 };
  const chip = (active) => ({
    flex: '0 0 auto', padding: '5px 10px', borderRadius: '14px', border: 'none',
    cursor: 'pointer', fontWeight: 600, fontSize: '12px', whiteSpace: 'nowrap',
    background: active ? '#2a5298' : '#fff', color: active ? '#fff' : '#333',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  });
  const tiny = { background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '10px', padding: '0 2px', opacity: 0.85 };

  return (
    <div className={'dialogs-container' + (activeUserId ? ' chat-open' : '')} style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: '15px', height: 'calc(100vh - 95px)', padding: '15px', boxSizing: 'border-box', minHeight: 0 }}>
      <div className="dialogs-list" style={{ background: 'rgba(255,255,255,0.65)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.4)', borderRadius: '10px', padding: '15px', overflowY: 'auto', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', minHeight: 0 }}>
        <h3 style={{ marginTop: 0 }}>Диалоги</h3>

        <div onClick={() => setActiveUserId(currentUser?.uid)} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px', borderRadius: '8px', cursor: 'pointer', background: isMonolog ? '#e3f2fd' : 'rgba(232,245,233,0.6)', marginBottom: '6px', border: '1px dashed #4caf50' }}>
          <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'linear-gradient(135deg,#4caf50,#2e7d32)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>📝</div>
          <div style={{ flex: 1 }}>
            <strong style={{ fontSize: '14px' }}>Монолог</strong>
            <p style={{ margin: 0, fontSize: '12px', color: '#888' }}>Заметки, файлы, напоминания</p>
          </div>
        </div>

        {users.length === 0 ? (
          <p style={{ color: '#888', fontSize: '14px' }}>Пока нет других пользователей.</p>
        ) : (
          users.map((user) => (
            <div key={user.id} onClick={() => setActiveUserId(user.id)} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px', borderRadius: '8px', cursor: 'pointer', background: activeUserId === user.id ? '#e3f2fd' : 'transparent' }}>
               <UserAvatar user={user} size={40} />
              <div style={{ flex: 1 }}>
                <strong style={{ fontSize: '14px' }}>{user.displayName || (user.email ? user.email.split('@')[0] : 'Пользователь')}</strong>
                <p style={{ margin: 0, fontSize: '12px', color: '#888' }}>Написать сообщение</p>
              </div>
              {(currentUser?.unreadFrom?.[user.id] || 0) > 0 && (
                <span style={{ background: '#ff4d4f', color: '#fff', borderRadius: '12px', padding: '2px 8px', fontSize: '12px', fontWeight: 'bold', minWidth: '22px', textAlign: 'center' }}>
                  {currentUser.unreadFrom[user.id]}
                </span>
              )}
            </div>
          ))
        )}
      </div>

      <div className="chat-window" style={{ background: 'rgba(255,255,255,0.65)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.4)', borderRadius: '10px', padding: '15px', display: 'flex', flexDirection: 'column', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', minHeight: 0 }}>
        {activeUser || isMonolog ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid #eee', paddingBottom: '10px', marginBottom: '10px' }}>
              <button className="back-btn" onClick={() => setActiveUserId(null)}>←</button>
              <h3 style={{ margin: 0 }}>
                {isMonolog ? '📝 Монолог' : `Чат с ${activeUser.displayName || (activeUser.email ? activeUser.email.split('@')[0] : 'пользователем')}`}
              </h3>
            </div>

            {isMonolog && (
              <>
                <div style={{ display: 'flex', gap: '6px', overflowX: 'auto', padding: '3px 0 8px 0', alignItems: 'center' }}>
                  <button onClick={() => setMonologTopic('all')} style={chip(monologTopic === 'all')}>📋 Все</button>
                  {monologTopics.map((t) => (
                    editMonologTopicId === t.id ? (
                      <span key={t.id} style={{ display: 'inline-flex', gap: '4px', alignItems: 'center', flex: '0 0 auto' }}>
                        <input value={editMonologTopicName} onChange={(e) => setEditMonologTopicName(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && saveEditMonologTopic(t.id)} autoFocus
                          style={{ width: '100px', padding: '4px 6px', borderRadius: '10px', border: '1px solid #2a5298', fontSize: '12px' }} />
                        <button onClick={() => saveEditMonologTopic(t.id)} style={tiny}>✅</button>
                        <button onClick={() => setEditMonologTopicId(null)} style={tiny}>✕</button>
                      </span>
                    ) : (
                      <span key={t.id} onClick={() => setMonologTopic(monologTopic === t.id ? 'all' : t.id)}
                        style={{ ...chip(monologTopic === t.id), display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                        # {t.name}
                        <button onClick={(e) => { e.stopPropagation(); setEditMonologTopicId(t.id); setEditMonologTopicName(t.name); }} title="Переименовать" style={tiny}>✏️</button>
                        <button onClick={(e) => { e.stopPropagation(); deleteMonologTopic(t.id); }} title="Удалить" style={tiny}>🗑️</button>
                      </span>
                    )
                  ))}
                  <button onClick={() => setShowMonologTopicForm((v) => !v)} title="Создать тему" style={{ ...chip(false), background: '#4caf50', color: '#fff' }}>＋</button>
                </div>
                {showMonologTopicForm && (
                  <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
                    <input value={newMonologTopicName} onChange={(e) => setNewMonologTopicName(e.target.value)} placeholder="Название темы"
                      onKeyPress={(e) => e.key === 'Enter' && createMonologTopic()}
                      style={{ flex: 1, padding: '6px 8px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '12px' }} />
                    <button onClick={createMonologTopic} style={{ padding: '6px 10px', background: '#4caf50', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}>Создать</button>
                  </div>
                )}
              </>
            )}

            <input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="🔍 Поиск в переписке..."
              style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '15px', marginBottom: '8px' }}
            />

            <div ref={chatScrollRef} style={{ flex: 1, overflowY: 'auto', padding: '10px', background: 'rgba(249,249,249,0.4)', borderRadius: '8px', marginBottom: '10px', minHeight: 0 }}>
              {filteredMessages.length === 0 ? (
                <p style={{ color: '#888', textAlign: 'center' }}>
                  {searchText.trim() ? 'Ничего не найдено.' : isMonolog ? 'Записей по этой теме пока нет.' : 'Сообщений пока нет. Напишите первым!'}
                </p>
              ) : (
                filteredMessages.map((msg) => {
                  const mine = msg.from === currentUser.uid;
                  const isEditing = editingMsgId === msg.id;
                  const bubbleStyle = {
                    display: 'inline-block', maxWidth: '70%', padding: '8px 12px', borderRadius: '15px',
                    background: mine ? '#0084ff' : '#e4e6eb', color: mine ? '#fff' : '#000', wordBreak: 'break-word',
                  };
                  return (
                    <div key={msg.id} style={{ textAlign: mine ? 'right' : 'left', marginBottom: '10px' }}>
                      <div style={{ fontSize: '10px', color: '#999', marginBottom: '2px' }}>
                        {formatDateTime(msg.createdAt)}
                      </div>
                      {isEditing ? (
                        <span style={{ display: 'inline-block', maxWidth: '80%' }}>
                          <input value={editText} onChange={(e) => setEditText(e.target.value)} style={{ width: '100%', padding: '6px 10px', borderRadius: '10px', border: '1px solid #4a90e2', boxSizing: 'border-box' }} />
                          <span style={{ display: 'flex', gap: '6px', marginTop: '4px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <input type="date" value={editDate} onChange={(e) => setEditDate(e.target.value)} style={{ padding: '3px 6px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '11px' }} />
                            <input type="time" value={editTime} onChange={(e) => setEditTime(e.target.value)} disabled={!editDate} style={{ padding: '3px 6px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '11px' }} />
                            {isMonolog && (
                              <select value={editMsgTopic} onChange={(e) => setEditMsgTopic(e.target.value)} style={{ padding: '3px 6px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '11px' }}>
                                <option value="">Без темы</option>
                                {monologTopics.map((t) => <option key={t.id} value={t.id}># {t.name}</option>)}
                              </select>
                            )}
                            <button onClick={() => saveEdit(msg)} style={smallBtn}>✅</button>
                            <button onClick={() => setEditingMsgId(null)} style={smallBtn}>❌</button>
                          </span>
                        </span>
                      ) : (
                        <>
                          {msg.type === 'file' ? (
                            <a href={msg.fileData} download={msg.fileName} style={{ ...bubbleStyle, textDecoration: 'none', fontWeight: 'bold', cursor: 'pointer' }}>📥 {msg.fileName}</a>
                          ) : (
                            <span style={{ ...bubbleStyle, display: 'inline-flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-start' }}>
                              <span>{msg.text}</span>
                              {msg.link && (
                                <a href={msg.link} target="_blank" rel="noopener noreferrer"
                                  style={{ 
                                    color: mine ? '#fff' : '#2a5298', 
                                    textDecoration: 'underline', 
                                    fontWeight: 'bold', 
                                    fontSize: '12px' 
                                  }}>
                                  🔗 Перейти по ссылке
                                </a>
                              )}
                              {msg.edited && <em style={{ fontSize: '11px', opacity: 0.7 }}> (изм.)</em>}
                            </span>
                          )}
                          {msg.eventDate && (
                            <span style={{ fontSize: '10px', background: '#e8f5e9', color: '#2e7d32', borderRadius: '8px', padding: '1px 6px', marginLeft: '4px', fontWeight: 700 }}>
                              📅 {msg.eventDate.split('-').reverse().join('.')}{msg.eventTime ? ` ⏰ ${msg.eventTime}` : ''}
                            </span>
                          )}
                          {isMonolog && msg.monologTopicId && (
                            <span style={{ fontSize: '10px', background: '#e3f2fd', color: '#1e3c72', borderRadius: '8px', padding: '1px 6px', marginLeft: '4px', fontWeight: 600 }}>
                              # {monologTopicNameById(msg.monologTopicId)}
                            </span>
                          )}

                          {!isMonolog && !isBlocked && REACTIONS.map((r) => {
                            const uids = (msg.reactions || {})[r.key] || [];
                            const active = uids.includes(currentUser?.uid);
                            return (
                              <button key={r.key} onClick={() => handleReactMessage(msg, r.key)} title={r.label}
                                style={{ ...smallBtn, opacity: active ? 1 : 0.5, fontSize: active ? '15px' : '13px' }}>
                                {r.emoji}{uids.length > 0 ? ` ${uids.length}` : ''}
                              </button>
                            );
                          })}

                          {!isBlocked && (mine || isAdmin) && (
                            <>
                              {mine && msg.type !== 'file' && <button onClick={() => startEdit(msg)} title="Редактировать" style={smallBtn}>✏️</button>}
                              <button onClick={() => handleDeleteMessage(msg)} title="Удалить" style={smallBtn}>🗑️</button>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            {isBlocked && (
              <div style={{ background: '#ffebee', color: '#c62828', padding: '8px 12px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>
                🚫 Вы заблокированы — доступен только просмотр переписки
              </div>
            )}
            {!isBlocked && (
            <>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '6px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '11px', color: '#888' }}>📅 Дата:</span>
              <input type="date" value={eventDate} onChange={(e) => setEventDate(e.target.value)} style={{ padding: '4px 6px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '11px' }} />
              <span style={{ fontSize: '11px', color: '#888' }}>⏰ Время:</span>
              <input type="time" value={eventTime} onChange={(e) => setEventTime(e.target.value)} disabled={!eventDate} style={{ padding: '4px 6px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '11px', opacity: eventDate ? 1 : 0.5 }} />
              {(eventDate || eventTime) && (
                <button onClick={() => { setEventDate(''); setEventTime(''); }} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#ff4d4f', fontWeight: 700 }}>✕</button>
              )}
              {isMonolog && (
                <span style={{ fontSize: '11px', color: '#2a5298', fontWeight: 600 }}>
                  Тема записи: {monologTopic === 'all' ? 'без темы' : '# ' + monologTopicNameById(monologTopic)}
                </span>
              )}
            </div>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <input type="file" ref={fileInputRef} style={{ display: 'none' }}
                onChange={(e) => { const f = e.target.files[0]; if (f) handleFileSend(f); e.target.value = ''; }} />
              <button onClick={() => fileInputRef.current && fileInputRef.current.click()} disabled={uploadingFile} title="Отправить файл (до 900 КБ)"
                style={{ padding: '10px 14px', background: '#f0f2f5', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '16px' }}>
                {uploadingFile ? '⏳' : '📎'}
              </button>
                            {window.innerWidth > 768 && (
                <button onClick={startVoice} title="Ввести голосом (только на компьютере)"
                  style={{ padding: '10px 14px', background: '#f0f2f5', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '16px' }}>🎤</button>
              )}
              <input type="text" value={newMessageText} onChange={handleTextChange}
                onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                placeholder="Сообщение..."
                style={{ flex: 1, padding: '10px', borderRadius: '5px', border: '1px solid #ccc' }} />
              <button onClick={handleSend} style={{ padding: '10px 20px', background: '#4a90e2', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>Отправить</button>
            </div>
            </>
            )}
          </>
        ) : (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888' }}>
            👈 Выберите пользователя слева, чтобы начать переписку
          </div>
        )}
      </div>
    </div>
  );
};

export default Dialogs;